import { useState, useEffect, useRef } from 'react';
import { getActiveMovements, getHistoryMovements, registerEntry, registerExit, type Movement, type RegisterExitData, type InvoiceClientData } from '../../api/movements.api';
import { useReactToPrint } from 'react-to-print';
import { TicketPrintTemplate } from '../../components/feature/tickets/TicketPrintTemplate';
import { useShift } from '../../hooks/useShift';
import { searchClientByDocument, type ParkingClient } from '../../api/clients.api';
import { getVehicleByPlaca } from '../../api/vehicles.api';
import { searchFactusMunicipalities, type FactusMunicipality } from '../../api/factus.api';
import { getPlateFormat, getPlateFormatInfo, filterRatesByPlateFormat, type PlateFormat } from '../../utils/plateValidation';
import { formatCOP, CurrencyInput } from '../../utils/currencyFormat';

export default function Movimientos() {
  const userRole = localStorage.getItem('userRole') || 'OPERADOR';
  const isAdmin = userRole === 'ADMIN';

  /* Módulos de Estado */
  const [showEntradaModal, setShowEntradaModal] = useState(false);
  const [showSalidaModal, setShowSalidaModal] = useState(false);
  const [showNoShiftWarning, setShowNoShiftWarning] = useState(false);
  const [showPrintSettings, setShowPrintSettings] = useState(false);
  const [showKioskGuide, setShowKioskGuide] = useState(false);
  
  /* Hook de Turno - Para verificar si hay turno activo */
  const { shift: activeShift, loading: loadingShift } = useShift();
  
  /* Estados Formulario */
  const [selectedPlaca, setSelectedPlaca] = useState(''); // Entrada
  const [contractVehicle, setContractVehicle] = useState<any | null>(null); // Vehículo contrato detectado
  const [checkingPlaca, setCheckingPlaca] = useState(false);
  const [searchPlaca, setSearchPlaca] = useState(''); // Salida
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false); // Alert global
  // Usamos 'any' temp para acomodar PreviewResponse que trae extra fields vs Movement
  const [vehiculoSalida, setVehiculoSalida] = useState<any | null>(null); 
  const [metodoPago, setMetodoPago] = useState<'EFECTIVO' | 'TARJETA' | 'TRANSFERENCIA'>('EFECTIVO');
  const [tipoTarjeta, setTipoTarjeta] = useState<'DEBITO' | 'CREDITO'>('DEBITO'); // Tipo de tarjeta
  const [montoRecibido, setMontoRecibido] = useState(0); // POS V2: Monto Recibido
  const [cambio, setCambio] = useState(0); // POS V2: Cambio global
  
  /* Estados Facturación Electrónica */
  const [invoiceType, setInvoiceType] = useState<'CONSUMIDOR_FINAL' | 'CLIENTE_IDENTIFICADO'>('CONSUMIDOR_FINAL');
  const [clienteDocumento, setClienteDocumento] = useState('');
  const [clienteData, setClienteData] = useState<InvoiceClientData | null>(null);
  const [clienteEncontrado, setClienteEncontrado] = useState<ParkingClient | null>(null);
  const [isSearchingClient, setIsSearchingClient] = useState(false);
  
  // Estados búsqueda ciudad
  const [citySuggestions, setCitySuggestions] = useState<FactusMunicipality[]>([]);
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);
  const [isSearchingCity, setIsSearchingCity] = useState(false);
  
  /* Estados Generales */
  const [movimientos, setMovimientos] = useState<Movement[]>([]);
  const [historyMovimientos, setHistoryMovimientos] = useState<Movement[]>([]);
  const [viewMode, setViewMode] = useState<'ACTIVE' | 'HISTORY'>(isAdmin ? 'HISTORY' : 'ACTIVE');
  const [isSubmitting, setIsSubmitting] = useState(false); // Global Loading

  // Print State
  const [ticketPrintData, setTicketPrintData] = useState<any | null>(null);
  const [printWidth, setPrintWidth] = useState<'50mm' | '80mm'>(() => {
    return (localStorage.getItem('printWidth') as '50mm' | '80mm') || '80mm';
  });
  const [autoPrint, setAutoPrint] = useState<boolean>(() => {
    return localStorage.getItem('autoPrint') === 'true';
  });
  const [quickSearch, setQuickSearch] = useState(''); // Nuevo input unificado
  const quickInputRef = useRef<HTMLInputElement>(null);
  const componentRef = useRef<HTMLDivElement>(null);
  
  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    onAfterPrint: () => setTicketPrintData(null)
  });

  const handleTestPrint = () => {
    setTicketPrintData({
      tipo: 'ENTRADA',
      nombreEmpresa: 'PARQIUM (PRUEBA)',
      nitEmpresa: '123456789-0',
      direccion: 'CALLE DE PRUEBA 123',
      numeroTicket: 'PRUEBA-1',
      placa: 'TEST-000',
      tipoVehiculo: 'AUTO',
      horaEntrada: new Date().toISOString(),
      operador: 'SISTEMA',
      infoAdicional: 'TICKET DE PRUEBA'
    });
    // Cerramos el modal de configuración para que se vea el de impresión
    setShowPrintSettings(false);
    // El useEffect se encargará de disparar la impresión si autoPrint es true, 
    // pero para la prueba queremos dispararlo siempre
    setTimeout(() => {
      handlePrint();
    }, 500);
  };

  // Trigger automático de impresión
  useEffect(() => {
    if (ticketPrintData && autoPrint) {
      // Delay mínimo para asegurar renderizado
      const timer = setTimeout(() => {
        handlePrint();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [ticketPrintData, autoPrint]);

  // Persistir configuraciones
  useEffect(() => {
    localStorage.setItem('printWidth', printWidth);
  }, [printWidth]);

  useEffect(() => {
    localStorage.setItem('autoPrint', autoPrint.toString());
  }, [autoPrint]);

  /* Datos POS Entrada */
  const [rates, setRates] = useState<any[]>([]); // TODO: Import Rate type
  const [selectedRate, setSelectedRate] = useState<any>(null);
  const [submittingRateId, setSubmittingRateId] = useState<string | number | null>(null);
  
  /* Estados Validación Automática de Placas */
  const [detectedPlateFormat, setDetectedPlateFormat] = useState<PlateFormat>(null);
  const [filteredRates, setFilteredRates] = useState<any[]>([]);

  useEffect(() => {
    loadMovements();
    loadRates();
  }, [viewMode]); // Reload when mode changes

  const loadRates = async () => {
      try {
          const loadedRates = await import('../../api/rates.api').then(r => r.getRates());
          setRates(loadedRates);
      } catch (error) {
          console.error('Error loading rates:', error);
      }
  };

  const loadMovements = async () => {
    try {
        if (viewMode === 'ACTIVE') {
            const data = await getActiveMovements();
            setMovimientos(data);
        } else {
            const data = await getHistoryMovements();
            setHistoryMovimientos(data);
        }
    } catch (error) {
        console.error('Error loading movements:', error);
    }
  };

  // Barcode Scanner Logic para Salida
  useEffect(() => {
    if (!showSalidaModal) return;

    let barcodeBuffer = '';
    let barcodeTimeout: NodeJS.Timeout;

    const handleKeyPress = (e: KeyboardEvent) => {
      // Ignorar si el usuario está escribiendo en un input de texto manualmente
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' && target !== document.activeElement) {
        return;
      }

      // Si es Enter, procesar el código escaneado
      if (e.key === 'Enter' && barcodeBuffer.length > 0) {
        e.preventDefault();
        const scannedCode = barcodeBuffer.trim().toUpperCase();
        
        // Limpiar buffer
        barcodeBuffer = '';
        clearTimeout(barcodeTimeout);

        // Buscar vehículo automáticamente
        if (scannedCode.length >= 3) {
          setSearchPlaca(scannedCode);
          // Trigger búsqueda automática
          setTimeout(async () => {
            setIsSubmitting(true);
            try {
              const preview = await import('../../api/movements.api').then(m => m.previewExit(scannedCode));
              setVehiculoSalida(preview);
              setMetodoPago('EFECTIVO');
              setMontoRecibido(0);
              setCambio(0);
            } catch (error: any) {
              console.error('Error preview:', error);
              alert(error.response?.data?.message || 'Error buscando vehículo o no tiene entrada activa');
              setVehiculoSalida(null);
            } finally {
              setIsSubmitting(false);
            }
          }, 100);
        }
        return;
      }

      // Acumular caracteres (típico de escáner de código de barras)
      if (e.key.length === 1) {
        barcodeBuffer += e.key;
        
        // Reset buffer después de 100ms de inactividad
        clearTimeout(barcodeTimeout);
        barcodeTimeout = setTimeout(() => {
          barcodeBuffer = '';
        }, 100);
      }
    };

    window.addEventListener('keypress', handleKeyPress);

    return () => {
      window.removeEventListener('keypress', handleKeyPress);
      clearTimeout(barcodeTimeout);
    };
  }, [showSalidaModal]);

  // Enter Key Handler para Confirmar Pago
  useEffect(() => {
    if (!vehiculoSalida) return;

    const handleEnterKey = (e: KeyboardEvent) => {
      // Solo activar si es la tecla Enter
      if (e.key !== 'Enter') return;

      // Ignorar si el usuario está escribiendo en un input o textarea
      // EXCEPCIÓN: Permitimos Enter si es para cobrar, a menos que sea un campo que ya maneja Enter (como búsqueda)
      const target = e.target as HTMLElement;
      if (target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') {
        return;
      }

      // Verificar que no esté deshabilitado el botón
      const isDisabled = 
        isSubmitting || 
        (!vehiculoSalida.movement.usaCupoContrato && metodoPago === 'EFECTIVO' && montoRecibido < (vehiculoSalida.total || 0)) ||
        (!vehiculoSalida.movement.usaCupoContrato && invoiceType === 'CLIENTE_IDENTIFICADO' && (!clienteData || !clienteData.nombre));

      if (!isDisabled) {
        e.preventDefault();
        handleConfirmarPago();
      }
    };

    window.addEventListener('keydown', handleEnterKey);

    return () => {
      window.removeEventListener('keydown', handleEnterKey);
    };
  }, [vehiculoSalida, isSubmitting, metodoPago, montoRecibido, invoiceType, clienteData]);

  // Lógica de Quick Search y Atajos Globales
  useEffect(() => {
    const handleGlobalKeys = (e: KeyboardEvent) => {
      // 1. Tecla ESC para limpiar todo y volver al input principal
      if (e.key === 'Escape') {
        setShowEntradaModal(false);
        setShowSalidaModal(false);
        setQuickSearch('');
        setVehiculoSalida(null);
        setSelectedPlaca('');
        setTimeout(() => quickInputRef.current?.focus(), 100);
        return;
      }

      // 2. Teclas Numéricas (1-9) para seleccionar tarifas en el modal de entrada
      if (showEntradaModal && !selectedRate && filteredRates.length > 0) {
        const num = parseInt(e.key);
        if (num >= 1 && num <= filteredRates.length) {
          e.preventDefault();
          handleEntradaConTarifa(filteredRates[num - 1]);
          return;
        }
      }

      // 3. F3 para forzar foco en el buscador rápido
      if (e.key === 'F3') {
        e.preventDefault();
        quickInputRef.current?.focus();
      }
    };

    window.addEventListener('keydown', handleGlobalKeys);
    return () => window.removeEventListener('keydown', handleGlobalKeys);
  }, [showEntradaModal, showSalidaModal, filteredRates, selectedRate]);

  // Función Unificada de Procesamiento (El "Cerebro" del flujo ágil)
  const handleQuickAction = async () => {
    const placa = quickSearch.trim().toUpperCase();
    if (!placa || placa.length < 3) return;

    setIsSubmitting(true);
    try {
      // 1. Verificar si el vehículo YA ESTÁ DENTRO (Salida)
      const isInside = movimientos.find(m => m.vehiculo.placa === placa && m.status === 'ACTIVO');
      
      if (isInside) {
        // FLUJO DE SALIDA
        const preview = await import('../../api/movements.api').then(m => m.previewExit(placa));
        setVehiculoSalida(preview);
        setSearchPlaca(placa);
        setShowSalidaModal(true);
        // El modal de salida tiene auto-foco en el monto o botón
      } else {
        // FLUJO DE ENTRADA
        setSelectedPlaca(placa);
        
        // Verificar contrato
        const vehicle = await getVehicleByPlaca(placa).catch(() => null);
        
        if (vehicle && vehicle.contractStatus === 'ACTIVO') {
          // Si tiene contrato activo, registrar entrada directo (máxima agilidad)
          setIsSubmitting(false); // Liberar para que handleEntradaContrato pueda ejecutarse
          setContractVehicle(vehicle);
          // Auto-trigger entrada contrato
          await handleEntradaContratoDirect(vehicle);
        } else {
          // No es contrato o está vencido, filtrar tarifas
          const compatible = filterRatesByPlateFormat(rates, placa);
          setFilteredRates(compatible);
          setDetectedPlateFormat(getPlateFormat(placa));
          
          if (compatible.length === 1) {
            // Solo una tarifa, entrar directo
            await handleEntradaConTarifaDirect(placa, compatible[0]);
          } else {
            // Múltiples tarifas o ninguna compatible, abrir modal
            setShowEntradaModal(true);
            if (vehicle) setContractVehicle(vehicle);
          }
        }
      }
    } catch (error: any) {
      console.error('Quick action error:', error);
      alert(error.response?.data?.message || 'Error procesando acción rápida');
    } finally {
      setIsSubmitting(false);
      setQuickSearch(''); // Limpiar para el siguiente
    }
  };

  // Auxiliares para ejecución directa sin modales si es posible
  const handleEntradaContratoDirect = async (vehicle: any) => {
    setIsSubmitting(true);
    try {
      const result = await registerEntry({ placa: vehicle.placa, tipo: vehicle.tipo });
      setSuccessMessage(`✅ Entrada (CONTRATO): ${result.ticket.vehiculo.placa}`);
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
      loadMovements();
      setTicketPrintData({
        tipo: 'ENTRADA',
        nombreEmpresa: result.ticket.company?.nombre || 'PARQIUM',
        nitEmpresa: result.ticket.company?.nit,
        direccion: result.ticket.company?.direccion,
        numeroTicket: result.ticket.numero,
        placa: result.ticket.vehiculo.placa,
        tipoVehiculo: result.ticket.vehiculo.tipo,
        horaEntrada: result.ticket.horaEntrada,
        operador: result.ticket.operador?.nombre || 'Operador',
        infoAdicional: 'CONTRATO',
      });
    } catch (error: any) {
      alert(error.response?.data?.message || 'Error en entrada contrato');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEntradaConTarifaDirect = async (placa: string, rate: any) => {
    setIsSubmitting(true);
    try {
      const result = await registerEntry({ placa, tipo: rate.tipoVehiculo });
      setSuccessMessage(`✅ Entrada: ${result.ticket.vehiculo.placa}`);
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
      loadMovements();
      setTicketPrintData({
        tipo: 'ENTRADA',
        nombreEmpresa: result.ticket.company?.nombre || 'PARQIUM',
        nitEmpresa: result.ticket.company?.nit,
        direccion: result.ticket.company?.direccion,
        numeroTicket: result.ticket.numero,
        placa: result.ticket.vehiculo.placa,
        tipoVehiculo: result.ticket.vehiculo.tipo,
        horaEntrada: result.ticket.horaEntrada,
        operador: result.ticket.operador?.nombre || 'Operador',
      });
    } catch (error: any) {
      alert(error.response?.data?.message || 'Error en entrada');
    } finally {
      setIsSubmitting(false);
    }
  };


  const handleReprint = (movement: Movement) => {
      if (!activeShift) {
          alert('Debe abrir un turno para realizar esta acción.');
          return;
      }
      console.log('Reprinting:', movement);
      
      if (movement.status === 'ACTIVO') {
          // Reprint Ticket Entrada
           setTicketPrintData({
            tipo: 'ENTRADA',
            nombreEmpresa: 'PARQIUM', // Idealmente vendría del backend
            numeroTicket: movement.id.slice(-6).toUpperCase(), // Fallback
            placa: movement.vehiculo.placa,
            tipoVehiculo: movement.vehiculo.tipo,
            horaEntrada: movement.horaEntrada,
            operador: movement.operador?.nombre || 'Operador',
            infoAdicional: movement.esContrato ? 'CONTRATO' : undefined,
        });
      } else if (movement.status === 'FINALIZADO') {
          // Reprint Ticket Salida / Factura
          if (movement.payment) {
              const payment = movement.payment;
              setTicketPrintData({
                tipo: 'SALIDA',
                nombreEmpresa: 'PARQIUM',
                numeroTicket: payment.boleto || payment.id.slice(-6),
                placa: movement.vehiculo.placa,
                tipoVehiculo: movement.vehiculo.tipo,
                horaEntrada: payment.horaEntrada,
                operador: movement.operador?.nombre || 'Operador',
                horaSalida: payment.horaSalida,
                duracion: payment.tiempo,
                tarifa: payment.tarifa,
                total: payment.total,
                metodoPago: payment.metodoPago,
                // Datos de factura electrónica si existen
                facturaElectronica: payment.electronicInvoice?.numero,
                cufe: payment.electronicInvoice?.cufe,
                qrInvoice: payment.electronicInvoice?.qrCode,
                infoAdicional: movement.esContrato ? 'CONTRATO' : undefined,
            });
          } else {
              alert('No se encontró información de pago para este movimiento.');
          }
      }
  };

  // Check vehicle status when typing and detect plate format
  const handlePlacaChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    setSelectedPlaca(val);
    
    // Detectar formato de placa y filtrar tarifas
    const format = getPlateFormat(val);
    setDetectedPlateFormat(format);
    
    if (val.length >= 3) {
      // Filtrar tarifas compatibles con el formato detectado
      const compatible = filterRatesByPlateFormat(rates, val);
      setFilteredRates(compatible);
      
      // Si hay exactamente 1 tarifa compatible, preparar auto-registro
      // (se ejecutará cuando checkingPlaca termine si no hay contrato)
    } else {
      setFilteredRates([]);
    }
    
    if (val.length >= 3) {
      setCheckingPlaca(true);
      try {
        const vehicle = await getVehicleByPlaca(val);
        if (vehicle.contractStatus !== 'NINGUNO') {
          setContractVehicle(vehicle); // Guarda el vehículo con info de contrato (activo o no)
        } else {
            setContractVehicle(null);
        }
      } catch (error) {
        // Ignorar 404
        setContractVehicle(null);
      } finally {
        setCheckingPlaca(false);
      }
    } else {
        setContractVehicle(null);
    }
  };

  // Auto-registrar entrada cuando hay 1 sola tarifa compatible
  const handleAutoEntry = async (rate: any) => {
    if (isSubmitting || !selectedPlaca) return;
    await handleEntradaConTarifa(rate);
  };

  const handleEntradaContrato = async () => {
    if (isSubmitting || !contractVehicle) return;
    setIsSubmitting(true);
    try {
        const result = await registerEntry({ 
            placa: contractVehicle.placa,
            tipo: contractVehicle.tipo
        });
        
        setShowEntradaModal(false);
        setSuccessMessage(`✅ Entrada (CONTRATO): ${result.ticket.vehiculo.placa}`);
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
        
        setSelectedPlaca('');
        setContractVehicle(null);
        loadMovements();

        setTicketPrintData({
             tipo: 'ENTRADA',
             nombreEmpresa: result.ticket.company?.nombre || 'PARQIUM',
             nitEmpresa: result.ticket.company?.nit,
             direccion: result.ticket.company?.direccion,
             numeroTicket: result.ticket.numero,
             placa: result.ticket.vehiculo.placa,
             tipoVehiculo: result.ticket.vehiculo.tipo,
             horaEntrada: result.ticket.horaEntrada,
             operador: result.ticket.operador?.nombre || 'Operador',
             infoAdicional: 'CONTRATO',
         });
    } catch (error: any) {
        console.error('Error contract entry:', error);
        alert(error.response?.data?.message || 'Error registrando entrada contrato');
    } finally {
        setIsSubmitting(false);
    }
  };

  // Handler Refactorizado para usar Tarifa seleccionada
  const handleEntradaConTarifa = async (rate: any) => {
    if (isSubmitting || !selectedPlaca) return;

    setIsSubmitting(true);
    setSubmittingRateId(rate.id);
    try {
        const result = await registerEntry({ 
            placa: selectedPlaca.toUpperCase(),
            tipo: rate.tipoVehiculo // Enviamos el tipo asociado a la tarifa
        });
        
        const isContract = result.movement.esContrato || !!result.contract;

        setShowEntradaModal(false);
        setSuccessMessage(isContract 
            ? `✅ Entrada (CONTRATO): ${result.ticket.vehiculo.placa}` 
            : `✅ Entrada registrada: ${result.ticket.vehiculo.placa}`
        );
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
        setSelectedPlaca('');
        setContractVehicle(null); // Reset
        setSelectedRate(null);
        loadMovements();
        
        // Show Print Modal
        setTicketPrintData({
            tipo: 'ENTRADA',
            nombreEmpresa: result.ticket.company?.nombre || 'PARQIUM',
            nitEmpresa: result.ticket.company?.nit,
            direccion: result.ticket.company?.direccion,
            numeroTicket: result.ticket.numero,
            placa: result.ticket.vehiculo.placa,
            tipoVehiculo: result.ticket.vehiculo.tipo,
            horaEntrada: result.ticket.horaEntrada,
            operador: result.ticket.operador?.nombre || 'Operador',
            infoAdicional: isContract ? 'CONTRATO' : undefined,
        });

    } catch (error: any) {
        console.error('Error entry:', error);
        alert(error.response?.data?.message || 'Error registrando entrada');
    } finally {
        setIsSubmitting(false);
        setSubmittingRateId(null);
    }
  };


  const handleBuscarVehiculoSalida = async () => {
    if (!searchPlaca.trim()) return;
    setIsSubmitting(true);
    try {
        const preview = await import('../../api/movements.api').then(m => m.previewExit(searchPlaca.toUpperCase()));
        setVehiculoSalida(preview);
        setMetodoPago('EFECTIVO'); // Reset default
        setMontoRecibido(0); // Reset monto
        setCambio(0); // Reset cambio
    } catch (error: any) {
        console.error('Error preview:', error);
        alert(error.response?.data?.message || 'Error buscando vehículo o no tiene entrada activa');
        setVehiculoSalida(null);
    } finally {
        setIsSubmitting(false);
    }
  };

  // Buscar cliente por documento para facturación
  const handleBuscarCliente = async () => {
    if (!clienteDocumento.trim() || clienteDocumento.length < 5) return;
    setIsSearchingClient(true);
    try {
        const client = await searchClientByDocument(clienteDocumento);
        if (client) {
            setClienteEncontrado(client);
            setClienteData({
                tipoDocumento: client.tipoDocumento,
                numeroDocumento: client.numeroDocumento,
                nombre: client.nombre,
                email: client.email,
                telefono: client.telefono,
                direccion: client.direccion,
                ciudad: client.ciudad,
                ciudadId: client.ciudadId,
                saveClient: false,
            });
        } else {
            setClienteEncontrado(null);
            setClienteData({
                tipoDocumento: 'CC',
                numeroDocumento: clienteDocumento,
                nombre: '',
                email: '',
                saveClient: true,
            });
        }
    } catch (error) {
        console.error('Error searching client:', error);
        setClienteEncontrado(null);
        setClienteData({
            tipoDocumento: 'CC',
            numeroDocumento: clienteDocumento,
            nombre: '',
            email: '',
            saveClient: true,
        });
    } finally {
        setIsSearchingClient(false);
    }
  };

  const handleConfirmarPago = async () => {
    if (!vehiculoSalida) return;
    setIsSubmitting(true);
    try {
        // Determinar el método de pago real (si es tarjeta, usar DEBITO o CREDITO)
        const metodoPagoReal = metodoPago === 'TARJETA' ? tipoTarjeta : metodoPago;
        
        // Construir datos de salida con facturación
        const exitData: RegisterExitData = {
            placa: vehiculoSalida.vehiculo.placa,
            metodoPago: metodoPagoReal,
            tarifaCobrada: vehiculoSalida.total,
            invoiceType: invoiceType,
        };

        // Si es cliente identificado, agregamos los datos
        if (invoiceType === 'CLIENTE_IDENTIFICADO' && clienteData) {
            exitData.clientData = clienteData;
        }

        const result = await registerExit(exitData);

        // Resetear estados
        setShowSalidaModal(false);
        setVehiculoSalida(null);
        setSearchPlaca('');
        setInvoiceType('CONSUMIDOR_FINAL');
        setClienteDocumento('');
        setClienteData(null);
        setClienteEncontrado(null);
        
        // Mensaje de éxito con info de factura
        let invoiceInfo = '';
        if (result.invoice?.estado === 'EMITIDA') {
            invoiceInfo = `\n📄 Factura: ${result.invoice.numero}`;
        } else if (result.invoice?.estado === 'PENDIENTE') {
            invoiceInfo = `\n⏳ Factura Pendiente DIAN`;
        }
        setSuccessMessage(`✅ Salida Exitosa: ${vehiculoSalida.vehiculo.placa}${invoiceInfo}`);
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 4000);
        
        // Preparar Datos Impresión Factura
        const payment = result.payment;
        setTicketPrintData({
            tipo: 'SALIDA',
            nombreEmpresa: 'PARQIUM',
            nitEmpresa: result.invoice?.clienteDocumento !== '222222222' ? result.invoice?.clienteDocumento : '',
            direccion: '',
            numeroTicket: payment.boleto || result.invoice?.numero || payment.id.slice(-6),
            placa: vehiculoSalida.vehiculo.placa,
            tipoVehiculo: vehiculoSalida.vehiculo.tipo,
            horaEntrada: payment.horaEntrada,
            operador: 'Operador',
            horaSalida: payment.horaSalida,
            duracion: payment.tiempo,
            tarifa: payment.tarifa,
            total: payment.total,
            metodoPago: payment.metodoPago,
            // Datos de factura electrónica
            facturaElectronica: result.invoice?.numero,
            cufe: result.invoice?.cufe,
            qrInvoice: result.invoice?.qrCode,
            infoAdicional: vehiculoSalida.movement.esContrato ? 'CONTRATO' : undefined,
        });

        loadMovements();

    } catch (error: any) {
        console.error('Error exit:', error);
        alert(error.response?.data?.message || 'Error registrando salida');
    } finally {
        setIsSubmitting(false);
    }
  };


  return (
    <>
      <div className="p-4 sm:p-6">
        {showSuccessMessage && (
          <div className="fixed top-4 right-4 z-50 bg-white border-l-4 border-teal-500 rounded-xl shadow-xl p-4 animate-slide-in max-w-md">
            <p className="text-sm font-medium text-slate-800 whitespace-pre-line">{successMessage}</p>
          </div>
        )}

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-slate-800">Movimientos</h1>
              <p className="text-slate-500 text-sm mt-1">{isAdmin ? 'Consulta de entradas y salidas' : 'Control de entradas y salidas'}</p>
            </div>
            {!isAdmin && (
              <button 
                onClick={() => setShowPrintSettings(true)}
                className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition"
                title="Configuración de Impresora"
              >
                <i className="ri-settings-3-line text-xl"></i>
              </button>
            )}
          </div>
          {!isAdmin && (
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              <button
                onClick={() => activeShift ? setShowEntradaModal(true) : setShowNoShiftWarning(true)}
                disabled={loadingShift}
                className={`w-full sm:w-auto px-4 py-2.5 rounded-xl flex items-center justify-center gap-2 transition whitespace-nowrap ${
                  activeShift 
                    ? 'bg-green-500 hover:bg-green-600 text-white cursor-pointer' 
                    : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                }`}
              >
                <i className="ri-login-box-line text-lg"></i>
                Registrar Entrada
              </button>
              <button
                onClick={() => activeShift ? setShowSalidaModal(true) : setShowNoShiftWarning(true)}
                disabled={loadingShift}
                className={`w-full sm:w-auto px-4 py-2.5 rounded-xl flex items-center justify-center gap-2 transition whitespace-nowrap ${
                  activeShift 
                    ? 'bg-orange-500 hover:bg-orange-600 text-white cursor-pointer' 
                    : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                }`}
              >
                <i className="ri-logout-box-line text-lg"></i>
                Registrar Salida
              </button>
            </div>
          )}
        </div>

        {/* Banner de advertencia cuando no hay turno activo */}
        {!isAdmin && !loadingShift && !activeShift && (
          <div className="mb-6 bg-amber-50 border-l-4 border-amber-400 rounded-r-xl p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-error-warning-line text-xl text-amber-600"></i>
            </div>
            <div className="flex-1">
              <p className="font-semibold text-amber-800">No hay turno activo</p>
              <p className="text-sm text-amber-600">Debes abrir un turno antes de registrar entradas o salidas. Usa el botón "Abrir Caja" en la barra superior.</p>
            </div>
          </div>
        )}

        {/* Quick Action Input Section */}
        {!isAdmin && (
        <div className="mb-6">
          <div className="relative group">
            <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none">
              <i className="ri-flashlight-line text-2xl text-slate-400 group-focus-within:text-blue-500 transition-colors"></i>
            </div>
            <input
              ref={quickInputRef}
              type="text"
              value={quickSearch}
              onChange={(e) => setQuickSearch(e.target.value.toUpperCase())}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleQuickAction();
                }
              }}
              placeholder="Escribe Placa o Escanea Ticket (Enter)"
              className="w-full pl-14 pr-6 py-6 bg-white border-2 border-slate-200 rounded-2xl shadow-sm outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-50/50 text-2xl font-black tracking-widest text-slate-800 placeholder:text-slate-300 placeholder:font-medium placeholder:tracking-normal transition-all"
              autoFocus
            />
            <div className="absolute inset-y-0 right-0 pr-4 flex items-center gap-2">
              <div className="hidden sm:flex items-center gap-1.5 px-2 py-1 bg-slate-100 rounded-md text-[10px] font-bold text-slate-400 border border-slate-200 uppercase">
                <span>Enter</span>
                <i className="ri-corner-down-left-line"></i>
              </div>
              <button
                onClick={handleQuickAction}
                disabled={isSubmitting || quickSearch.length < 3}
                className="p-3 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-200 text-white rounded-xl transition shadow-lg shadow-blue-100 disabled:shadow-none"
              >
                {isSubmitting ? <i className="ri-loader-4-line animate-spin text-xl"></i> : <i className="ri-arrow-right-line text-xl font-bold"></i>}
              </button>
            </div>
          </div>
          <p className="mt-2 text-[11px] text-slate-400 px-2 flex items-center gap-4">
            <span className="flex items-center gap-1"><kbd className="px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded text-[10px]">ESC</kbd> Limpiar</span>
            <span className="flex items-center gap-1"><kbd className="px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded text-[10px]">F3</kbd> Enfocar</span>
            <span className="flex items-center gap-1"><kbd className="px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded text-[10px]">1-9</kbd> Seleccionar Tarifa</span>
          </p>
        </div>
        )}

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
              <h3 className="text-base sm:text-lg font-bold text-slate-800">
                  {viewMode === 'ACTIVE' ? 'Vehículos Dentro' : 'Historial Reciente'}
              </h3>
              <div className="flex bg-slate-100 p-1 rounded-lg">
                  <button
                      onClick={() => setViewMode('ACTIVE')}
                      className={`px-2 sm:px-3 py-1.5 text-xs sm:text-sm font-medium rounded-md transition ${viewMode === 'ACTIVE' ? 'bg-white shadow text-slate-800' : 'text-slate-500 hover:text-slate-700'}`}
                  >
                      Activos
                  </button>
                  <button
                      onClick={() => setViewMode('HISTORY')}
                      className={`px-3 py-1.5 text-sm font-medium rounded-md transition ${viewMode === 'HISTORY' ? 'bg-white shadow text-slate-800' : 'text-slate-500 hover:text-slate-700'}`}
                  >
                      Historial
                  </button>
              </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Placa</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Tipo</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Entrada</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Estado</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Operador</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600">{isAdmin ? '' : 'Acciones'}</th>
                </tr>
              </thead>
              <tbody>
                {(viewMode === 'ACTIVE' ? movimientos : historyMovimientos).length === 0 ? (
                    <tr><td colSpan={6} className="text-center py-6 text-slate-500">No hay movimientos para mostrar</td></tr>
                ) : (
                    (viewMode === 'ACTIVE' ? movimientos : historyMovimientos).map(mov => (
                  <tr key={mov.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                          <span className="font-semibold text-slate-800">{mov.vehiculo.placa}</span>
                          {mov.esContrato && (
                              <span 
                                className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold border ${
                                  mov.usaCupoContrato 
                                    ? 'bg-green-100 text-green-700 border-green-200' 
                                    : 'bg-orange-100 text-orange-700 border-orange-200'
                                }`} 
                                title={mov.usaCupoContrato ? "Usa cupo gratuito del contrato" : "Contrato - Paga tarifa normal (cupo ocupado)"}
                              >
                                {mov.usaCupoContrato ? 'CONTRATO ✓' : 'CONTRATO ⚠'}
                              </span>
                          )}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-700 rounded-lg text-xs font-medium">
                        {mov.vehiculo.tipo}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-700">
                        <div>{new Date(mov.horaEntrada).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })}</div>
                        <div className="text-xs text-slate-500">{new Date(mov.horaEntrada).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}</div>
                        {mov.horaSalida && <div className="text-xs text-slate-400">Salida: {new Date(mov.horaSalida).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}</div>}
                    </td>
                    <td className="py-3 px-4">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${
                            mov.status === 'ACTIVO' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                        }`}>
                          <i className={`ri-${mov.status === 'ACTIVO' ? 'time-line' : 'checkbox-circle-line'}`}></i>
                          {mov.status}
                        </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-700">{mov.operador?.nombre || 'Sistema'}</td>
                    <td className="py-3 px-4 text-right">
                        {!isAdmin && (
                        <button
                            onClick={() => handleReprint(mov)}
                            disabled={!activeShift}
                            className={`p-2 rounded-lg transition ${
                                activeShift 
                                ? 'text-slate-400 hover:text-blue-600 hover:bg-blue-50 cursor-pointer' 
                                : 'text-slate-200 cursor-not-allowed'
                            }`}
                            title={activeShift ? "Reimprimir Ticket" : "Turno cerrado"}
                        >
                            <i className="ri-printer-line text-lg"></i>
                        </button>
                        )}
                    </td>
                  </tr>
                )))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal Configuración Impresión */}
      {showPrintSettings && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="bg-slate-800 text-white px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <i className="ri-printer-line"></i>
                Configurar Impresora
              </h2>
              <button onClick={() => setShowPrintSettings(false)} className="text-slate-400 hover:text-white transition">
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            <div className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-3">Ancho de Papel</label>
                <div className="flex bg-slate-100 p-1 rounded-xl">
                  <button 
                    onClick={() => setPrintWidth('50mm')}
                    className={`flex-1 py-2 text-sm font-bold rounded-lg transition ${printWidth === '50mm' ? 'bg-white shadow-sm text-blue-600' : 'text-slate-500'}`}
                  >
                    50mm
                  </button>
                  <button 
                    onClick={() => setPrintWidth('80mm')}
                    className={`flex-1 py-2 text-sm font-bold rounded-lg transition ${printWidth === '80mm' ? 'bg-white shadow-sm text-blue-600' : 'text-slate-500'}`}
                  >
                    80mm
                  </button>
                </div>
                <p className="text-[10px] text-slate-400 mt-2 italic">Epson T20 usualmente usa 80mm.</p>
              </div>

              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-100">
                <div>
                  <label className="block text-sm font-bold text-slate-800">Impresión Automática</label>
                  <p className="text-[11px] text-slate-500">Imprimir al cobrar o ingresar</p>
                </div>
                <button 
                  onClick={() => {
                    const next = !autoPrint;
                    setAutoPrint(next);
                    if (next) setShowKioskGuide(true);
                  }}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${autoPrint ? 'bg-blue-600' : 'bg-slate-300'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${autoPrint ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>

              <div className="pt-2 border-t border-slate-100">
                <button
                  onClick={handleTestPrint}
                  className="w-full py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-bold transition flex items-center justify-center gap-2 mb-3"
                >
                  <i className="ri-test-tube-line"></i>
                  Imprimir Ticket de Prueba
                </button>
                
                <button
                  onClick={() => setShowPrintSettings(false)}
                  className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold shadow-lg shadow-blue-100 transition"
                >
                  Guardar y Cerrar
                </button>
              </div>

              <p className="text-[10px] text-slate-400 text-center px-4 leading-normal">
                Usa el botón de prueba para seleccionar tu impresora <b>Epson T20</b> en la ventana que aparecerá. El navegador la recordará para las siguientes impresiones.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Modal Guía Modo Kiosco */}
      {showKioskGuide && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[90] p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md my-auto overflow-hidden animate-in fade-in slide-in-from-bottom-8 duration-300">
            <div className="bg-blue-600 text-white p-5 sm:p-6 text-center">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-white/30">
                <i className="ri-printer-fill text-3xl sm:text-4xl"></i>
              </div>
              <h2 className="text-xl sm:text-2xl font-black">Impresión Directa</h2>
              <p className="text-blue-100 text-xs sm:text-sm mt-1">Configuración del Modo Kiosco</p>
            </div>
            
            <div className="p-6">
              <p className="text-slate-600 text-sm leading-relaxed mb-6">
                Para que los tickets se impriman <b>instantáneamente</b> sin mostrar el cuadro de diálogo de Windows/Mac, debes activar el <b>Modo Kiosco</b> en tu navegador Chrome.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center font-bold flex-shrink-0">1</div>
                  <p className="text-xs text-slate-700">Cierra todas las ventanas de Chrome.</p>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center font-bold flex-shrink-0">2</div>
                  <p className="text-xs text-slate-700">Haz clic derecho en el acceso directo de Chrome y ve a <b>Propiedades</b>.</p>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center font-bold flex-shrink-0 text-sm">3</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-slate-700 mb-2">Añade este comando (Windows) o usa la terminal (Mac):</p>
                    <div className="space-y-3">
                      <div>
                        <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Windows (Propiedades → Destino):</span>
                        <code className="block bg-slate-900 text-green-400 p-2 rounded-lg text-[10px] font-mono select-all overflow-x-auto whitespace-pre-wrap sm:whitespace-nowrap mt-1 border border-slate-800">
                          --kiosk-printing
                        </code>
                      </div>
                      <div>
                        <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Mac (Abrir Terminal):</span>
                        <code className="block bg-slate-900 text-blue-400 p-2 rounded-lg text-[10px] font-mono select-all overflow-x-auto whitespace-pre-wrap sm:whitespace-nowrap mt-1 border border-slate-800">
                          open -a "Google Chrome" --args --kiosk-printing
                        </code>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center font-bold flex-shrink-0">4</div>
                  <p className="text-xs text-slate-700">Dale a <b>Aceptar</b> y abre Chrome usando ese acceso directo.</p>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                <div className="flex gap-3">
                  <i className="ri-information-line text-amber-600 text-xl"></i>
                  <p className="text-[11px] text-amber-800 leading-normal">
                    <b>Importante:</b> Antes de esto, usa el botón de <b>Prueba de Impresión</b> para asegurarte de que tu impresora esté seleccionada como la impresora predeterminada del sistema.
                  </p>
                </div>
              </div>

              <button
                onClick={() => setShowKioskGuide(false)}
                className="w-full py-4 bg-slate-900 hover:bg-black text-white rounded-xl font-bold transition shadow-xl"
              >
                ¡Entendido, continuar!
              </button>
            </div>
          </div>
        </div>
      )}

       {/* Modal Entrada Refactorizado */}
      {showEntradaModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
            <div className="bg-blue-600 text-white px-6 py-4">
               <h2 className="text-xl font-bold flex items-center gap-2">
                 <i className="ri-car-line"></i>
                 Registrar Entrada
               </h2>
            </div>
            
            <div className="p-6">
                {/* Paso 1: Ingreso de Placa */}
                {!selectedRate && (
                     <form onSubmit={(e) => { 
                         e.preventDefault(); 
                         // Si hay contrato activo, registrar con contrato
                         if (contractVehicle && !isSubmitting) {
                             handleEntradaContrato();
                         }
                         // Si hay 1 sola tarifa filtrada, auto-registrar
                         else if (filteredRates.length === 1 && detectedPlateFormat && !isSubmitting) {
                             handleAutoEntry(filteredRates[0]);
                         }
                     }}>
                        <label className="block text-sm font-bold text-slate-700 mb-2 uppercase tracking-wide">
                            Paso 1: Ingrese Placa del Vehículo
                        </label>
                        <input
                            type="text"
                            value={selectedPlaca}
                            onChange={handlePlacaChange}
                            className={`w-full px-4 py-4 text-3xl font-black text-center border-2 border-slate-300 rounded-xl outline-none uppercase tracking-widest mb-2 ${
                                contractVehicle ? 'border-green-500 ring-4 ring-green-100' : 'focus:ring-4 focus:ring-blue-200 focus:border-blue-500'
                            }`}
                            placeholder="ABC-123"
                            maxLength={7}
                            autoFocus
                        />
                        
                        {checkingPlaca && (
                            <p className="text-center text-sm text-slate-500 mb-4 animate-pulse">
                                Verificando placa...
                            </p>
                        )}

                        {contractVehicle && contractVehicle.contractStatus === 'ACTIVO' && (
                            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6 animate-in fade-in zoom-in-95 duration-200">
                                <div className="flex items-center gap-3 mb-2">
                                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                        <i className="ri-checkbox-circle-line text-xl text-green-600"></i>
                                    </div>
                                    <div>
                                        <p className="font-bold text-green-800">Contrato Activo</p>
                                        <p className="text-xs text-green-600">Vehículo autorizado para ingreso</p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={handleEntradaContrato}
                                    disabled={isSubmitting}
                                    className="w-full py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold flex items-center justify-center gap-2 transition shadow-lg shadow-green-200 mt-2"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <i className="ri-loader-4-line animate-spin"></i>
                                            Procesando...
                                        </>
                                    ) : (
                                        <>
                                            <i className="ri-login-box-line"></i>
                                            Registrar Entrada (Contrato)
                                        </>
                                    )}
                                </button>

                            </div>
                        )}

                        {contractVehicle && (contractVehicle.contractStatus === 'VENCIDO' || contractVehicle.contractStatus === 'CANCELADO') && (
                            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 animate-in fade-in zoom-in-95 duration-200">
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                                        <i className="ri-error-warning-line text-xl text-amber-600"></i>
                                    </div>
                                    <div>
                                        <p className="font-bold text-amber-800">Contrato {contractVehicle.contractStatus}</p>
                                        <p className="text-xs text-amber-600">
                                            Cliente: {contractVehicle.relevantContract?.cliente}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setContractVehicle(null)}
                                        className="flex-1 py-2 bg-white border border-amber-300 text-amber-700 rounded-lg text-sm font-semibold hover:bg-amber-50 transition"
                                    >
                                        Generar Cobro Tarifa
                                    </button>
                                </div>
                            </div>
                        )}
                        
                        {!contractVehicle && selectedPlaca.length >= 3 && !checkingPlaca && (
                            <div className="space-y-3 animate-in fade-in slide-in-from-bottom-4 duration-300">
                                {/* Indicador de formato detectado */}
                                {detectedPlateFormat && (
                                    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg mb-2 ${
                                        detectedPlateFormat === 'MOTO' 
                                            ? 'bg-purple-50 border border-purple-200' 
                                            : 'bg-blue-50 border border-blue-200'
                                    }`}>
                                        <i className={`${getPlateFormatInfo(selectedPlaca)?.icon} text-lg ${
                                            detectedPlateFormat === 'MOTO' ? 'text-purple-600' : 'text-blue-600'
                                        }`}></i>
                                        <span className={`text-sm font-medium ${
                                            detectedPlateFormat === 'MOTO' ? 'text-purple-700' : 'text-blue-700'
                                        }`}>
                                            Formato detectado: {getPlateFormatInfo(selectedPlaca)?.label}
                                        </span>
                                    </div>
                                )}

                                {/* Advertencia si formato no válido */}
                                {!detectedPlateFormat && selectedPlaca.length >= 3 && (
                                    <div className="flex items-center gap-2 px-3 py-2 rounded-lg mb-2 bg-amber-50 border border-amber-200">
                                        <i className="ri-error-warning-line text-lg text-amber-600"></i>
                                        <span className="text-sm font-medium text-amber-700">
                                            Formato de placa no reconocido. Verifique que sea AAA123 (vehículo) o AAA12A (moto).
                                        </span>
                                    </div>
                                )}

                                {/* Auto-registro si hay 1 sola tarifa compatible */}
                                {filteredRates.length === 1 && detectedPlateFormat && (
                                    <div className="bg-green-50 border border-green-200 rounded-xl p-4 animate-in fade-in zoom-in-95 duration-200">
                                        <div className="flex items-center gap-3 mb-3">
                                            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                                <i className="ri-flashlight-line text-xl text-green-600"></i>
                                            </div>
                                            <div>
                                                <p className="font-bold text-green-800">Tarifa Automática</p>
                                                <p className="text-xs text-green-600">
                                                    {filteredRates[0].tipoVehiculo} - {formatCOP(filteredRates[0].valorFraccion)}/Fracción
                                                </p>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => handleAutoEntry(filteredRates[0])}
                                            disabled={isSubmitting}
                                            className="w-full py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold flex items-center justify-center gap-2 transition shadow-lg shadow-green-200"
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <i className="ri-loader-4-line animate-spin"></i>
                                                    Procesando...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="ri-login-box-line"></i>
                                                    Registrar Entrada
                                                </>
                                            )}
                                        </button>
                                    </div>
                                )}

                                {/* Selector de tarifas si hay múltiples opciones */}
                                {filteredRates.length > 1 && detectedPlateFormat && (
                                    <>
                                        <label className="block text-sm font-bold text-slate-700 mb-2 uppercase tracking-wide">
                                            Paso 2: Seleccione Tarifa
                                        </label>
                                        <div className="grid grid-cols-2 gap-3">
                                            {filteredRates.map((rate) => {
                                              const isThisRateSubmitting = isSubmitting && submittingRateId === rate.id;
                                              return (
                                                <button
                                                    key={rate.id}
                                                    type="button"
                                                    disabled={isSubmitting}
                                                    onClick={() => handleEntradaConTarifa(rate)}
                                                    className={`relative group p-4 border-2 rounded-xl transition text-left ${
                                                        isThisRateSubmitting 
                                                            ? 'border-blue-500 bg-blue-50 cursor-wait'
                                                            : isSubmitting 
                                                                ? 'border-slate-100 opacity-50 cursor-not-allowed'
                                                                : 'border-slate-100 hover:border-blue-500 hover:bg-blue-50'
                                                    }`}
                                                >
                                                    {isThisRateSubmitting ? (
                                                        <div className="flex flex-col items-center justify-center h-full py-2">
                                                            <i className="ri-loader-4-line text-3xl text-blue-600 animate-spin mb-2"></i>
                                                            <span className="text-sm font-bold text-blue-600 animate-pulse">Procesando...</span>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <div className="text-2xl mb-1 text-slate-700 group-hover:text-blue-600">
                                                                {rate.tipoVehiculo === 'MOTO' ? <i className="ri-motorbike-line"></i> : 
                                                                 rate.tipoVehiculo === 'AUTO' ? <i className="ri-car-fill"></i> : 
                                                                 <i className="ri-truck-line"></i>}
                                                            </div>
                                                            <div className="font-bold text-slate-800">{rate.tipoVehiculo}</div>
                                                            <div className="text-xs text-slate-500 font-medium">
                                                                formatCOP(rate.valorFraccion) / Fracción
                                                            </div>
                                                        </>
                                                    )}
                                                </button>
                                              );
                                            })}
                                        </div>
                                    </>
                                )}

                                {/* Sin tarifas compatibles */}
                                {filteredRates.length === 0 && detectedPlateFormat && (
                                    <div className="col-span-2 text-center p-4 bg-orange-50 text-orange-600 rounded-lg text-sm">
                                        ⚠️ No hay tarifas configuradas para {detectedPlateFormat === 'MOTO' ? 'motos' : 'vehículos'}. 
                                        <br/>Contacte al administrador.
                                    </div>
                                )}

                                {/* Mostrar todas las tarifas si formato no válido */}
                                {!detectedPlateFormat && selectedPlaca.length >= 3 && rates.length > 0 && (
                                    <>
                                        <label className="block text-sm font-bold text-slate-700 mb-2 uppercase tracking-wide">
                                            Seleccione Tarifa Manualmente
                                        </label>
                                        <div className="grid grid-cols-2 gap-3">
                                            {rates.filter(r => r.activo).map((rate) => {
                                              const isThisRateSubmitting = isSubmitting && submittingRateId === rate.id;
                                              return (
                                                <button
                                                    key={rate.id}
                                                    type="button"
                                                    disabled={isSubmitting}
                                                    onClick={() => handleEntradaConTarifa(rate)}
                                                    className={`relative group p-4 border-2 rounded-xl transition text-left ${
                                                        isThisRateSubmitting 
                                                            ? 'border-blue-500 bg-blue-50 cursor-wait'
                                                            : isSubmitting 
                                                                ? 'border-slate-100 opacity-50 cursor-not-allowed'
                                                                : 'border-slate-100 hover:border-blue-500 hover:bg-blue-50'
                                                    }`}
                                                >
                                                    {isThisRateSubmitting ? (
                                                        <div className="flex flex-col items-center justify-center h-full py-2">
                                                            <i className="ri-loader-4-line text-3xl text-blue-600 animate-spin mb-2"></i>
                                                            <span className="text-sm font-bold text-blue-600 animate-pulse">Procesando...</span>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <div className="text-2xl mb-1 text-slate-700 group-hover:text-blue-600">
                                                                {rate.tipoVehiculo === 'MOTO' ? <i className="ri-motorbike-line"></i> : 
                                                                 rate.tipoVehiculo === 'AUTO' ? <i className="ri-car-fill"></i> : 
                                                                 <i className="ri-truck-line"></i>}
                                                            </div>
                                                            <div className="font-bold text-slate-800">{rate.tipoVehiculo}</div>
                                                            <div className="text-xs text-slate-500 font-medium">
                                                                {formatCOP(rate.valorFraccion)} / Fracción
                                                            </div>
                                                        </>
                                                    )}
                                                </button>
                                              );
                                            })}
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                        
                         <button
                            type="button"
                            onClick={() => { setShowEntradaModal(false); setSelectedPlaca(''); setContractVehicle(null); setDetectedPlateFormat(null); setFilteredRates([]); }}
                            className="w-full mt-6 py-3 text-slate-500 font-medium hover:text-slate-700"
                        >
                            Cancelar
                        </button>
                    </form>
                )}
            </div>
          </div>
        </div>
      )}

      {/* Contenedor de Impresión (Fuera de vista para impresión silenciosa) */}
      <div className="fixed -left-[9999px] -top-[9999px]" aria-hidden="true">
        {ticketPrintData && (
          <TicketPrintTemplate 
            ref={componentRef} 
            data={ticketPrintData}
            width={printWidth} 
          />
        )}
      </div>

      {/* Modal Informativo/Manual (Solo se muestra si NO hay impresión automática o es prueba) */}
      {ticketPrintData && (!autoPrint || ticketPrintData.infoAdicional === 'TICKET DE PRUEBA') && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[80] p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden">
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-check-line text-3xl text-green-600"></i>
              </div>
               <h3 className="text-xl font-bold text-slate-800 mb-2">
                 {ticketPrintData.infoAdicional === 'TICKET DE PRUEBA' ? 'Ticket de Prueba' : 'Ticket Generado'}
               </h3>
               <p className="text-slate-500 text-sm mb-6">
                 {ticketPrintData.infoAdicional === 'TICKET DE PRUEBA' 
                   ? 'Vistazo previo del formato seleccionado.' 
                   : 'El ticket ha sido generado correctamente.'}
               </p>
              
              <div className="flex bg-slate-100 p-1 rounded-xl mb-6">
                  <button 
                    onClick={() => setPrintWidth('50mm')}
                    className={`flex-1 py-1.5 text-sm font-medium rounded-lg transition ${printWidth === '50mm' ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500'}`}
                  >
                    50mm
                  </button>
                  <button 
                    onClick={() => setPrintWidth('80mm')}
                    className={`flex-1 py-1.5 text-sm font-medium rounded-lg transition ${printWidth === '80mm' ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500'}`}
                  >
                    80mm
                  </button>
              </div>

              {/* Preview Container - Solo para previsualización manual */}
              <div className="border border-slate-200 rounded-lg p-4 mb-4 bg-slate-50 overflow-auto max-h-[300px] flex justify-center">
                  <TicketPrintTemplate 
                    data={ticketPrintData}
                    width={printWidth} 
                  />
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setTicketPrintData(null)}
                  className="flex-1 px-4 py-3 border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 font-medium"
                >
                  Cerrar
                </button>
                <button
                  type="button"
                  onClick={handlePrint}
                  className="flex-1 px-4 py-3 bg-slate-800 hover:bg-slate-900 text-white rounded-xl font-medium flex items-center justify-center gap-2"
                >
                  <i className="ri-printer-line"></i>
                  Imprimir
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Salida - POS */}
      {showSalidaModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col my-auto">
            <div className="bg-orange-500 text-white px-6 py-4 rounded-t-2xl flex-shrink-0">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <i className="ri-logout-box-line"></i>
                Registrar Salida & Cobro
              </h2>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1">
                {!vehiculoSalida ? (
                    /* PASO 1: BUSCAR PLACA */
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">Buscar Placa de Salida</label>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={searchPlaca}
                                    onChange={(e) => setSearchPlaca(e.target.value.toUpperCase())}
                                    onKeyDown={(e) => e.key === 'Enter' && handleBuscarVehiculoSalida()}
                                    className="flex-1 px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-orange-500 outline-none text-lg font-semibold text-center uppercase"
                                    placeholder="ABC123"
                                    autoFocus
                                />
                                <button
                                    onClick={handleBuscarVehiculoSalida}
                                    disabled={isSubmitting}
                                    className="px-4 py-3 bg-orange-100 text-orange-600 hover:bg-orange-200 rounded-xl transition disabled:opacity-50"
                                >
                                    {isSubmitting ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-search-line font-bold"></i>}
                                </button>
                            </div>
                        </div>
                        <button
                            onClick={() => setShowSalidaModal(false)}
                            className="w-full mt-2 px-4 py-3 border border-slate-100 text-slate-500 rounded-xl hover:bg-slate-50 font-medium"
                        >
                            Cancelar
                        </button>
                    </div>
                ) : (
                    /* PASO 2: RESUMEN Y PAGO */
                    <div className="space-y-6">
                        {/* Resumen Ticket */}
                        <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 space-y-2">
                            <div className="flex justify-between items-center border-b border-slate-200 pb-2 mb-2">
                                <span className="text-slate-500 text-sm">Placa</span>
                                <span className="text-xl font-bold text-slate-800">{vehiculoSalida.vehiculo.placa}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-slate-500">Entrada</span>
                                <span className="text-slate-700 font-medium">{new Date(vehiculoSalida.horaEntrada).toLocaleTimeString()}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-slate-500">Salida</span>
                                <span className="text-slate-700 font-medium">{new Date(vehiculoSalida.horaSalida).toLocaleTimeString()}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-slate-500">Duración Total</span>
                                <span className="text-slate-700 font-medium">{vehiculoSalida.duracion} ({vehiculoSalida.duracionMinutos} min)</span>
                            </div>
                            
                            {/* Desglose Detallado de Cálculo */}
                            <div className="bg-white rounded-lg p-3 mt-3 border border-slate-200">
                                <p className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1">
                                    <i className="ri-calculator-line"></i>
                                    Desglose de Cobro:
                                </p>
                                <div className="text-xs text-slate-600 space-y-1">
                                    <p className="text-[11px] leading-relaxed">{/* @ts-ignore */ vehiculoSalida.breakdown}</p>
                                </div>
                            </div>

                            <div className="flex justify-between items-center pt-2 mt-2 border-t border-slate-200">
                                <span className="font-bold text-slate-700">Total a Pagar</span>
                                <span className="text-2xl font-black text-green-600">{formatCOP(vehiculoSalida.total)}</span>
                            </div>
                        </div>

                        {/* Formulario Pago */}
                        {/* Formulario Pago o Info Contrato */}
                        {!vehiculoSalida.movement.usaCupoContrato ? (
                            <>
                                <div className="space-y-4">
                                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider">Método de Pago</label>
                                    <div className="grid grid-cols-2 gap-2">
                                        <button
                                            onClick={() => setMetodoPago('EFECTIVO')}
                                            className={`px-3 py-2 rounded-lg border text-sm font-medium transition flex items-center justify-center gap-2 ${metodoPago === 'EFECTIVO' ? 'bg-green-50 border-green-200 text-green-700 ring-1 ring-green-200' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}
                                        >
                                            <i className="ri-money-dollar-circle-line"></i> Efectivo
                                        </button>
                                        <button
                                            onClick={() => setMetodoPago('TARJETA')}
                                            className={`px-3 py-2 rounded-lg border text-sm font-medium transition flex items-center justify-center gap-2 ${metodoPago === 'TARJETA' ? 'bg-blue-50 border-blue-200 text-blue-700 ring-1 ring-blue-200' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}
                                        >
                                            <i className="ri-bank-card-line"></i> Tarjeta
                                        </button>
                                        <button
                                            onClick={() => setMetodoPago('TRANSFERENCIA')}
                                            className={`px-3 py-2 rounded-lg border text-sm font-medium transition flex items-center justify-center gap-2 col-span-2 ${metodoPago === 'TRANSFERENCIA' ? 'bg-purple-50 border-purple-200 text-purple-700 ring-1 ring-purple-200' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}
                                        >
                                            <i className="ri-bank-line"></i> Transferencia Bancaria
                                        </button>
                                    </div>

                                    {/* Selector Débito/Crédito cuando se selecciona Tarjeta */}
                                    {metodoPago === 'TARJETA' && (
                                        <div className="animate-in fade-in slide-in-from-top-2 duration-200">
                                            <label className="block text-xs font-medium text-slate-500 mb-2">Tipo de Tarjeta:</label>
                                            <div className="grid grid-cols-2 gap-2">
                                                <button
                                                    onClick={() => setTipoTarjeta('DEBITO')}
                                                    className={`px-3 py-2 rounded-lg border text-sm font-medium transition flex items-center justify-center gap-2 ${tipoTarjeta === 'DEBITO' ? 'bg-cyan-50 border-cyan-300 text-cyan-700 ring-1 ring-cyan-200' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}
                                                >
                                                    <i className="ri-bank-card-2-line"></i> Débito
                                                </button>
                                                <button
                                                    onClick={() => setTipoTarjeta('CREDITO')}
                                                    className={`px-3 py-2 rounded-lg border text-sm font-medium transition flex items-center justify-center gap-2 ${tipoTarjeta === 'CREDITO' ? 'bg-amber-50 border-amber-300 text-amber-700 ring-1 ring-amber-200' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}
                                                >
                                                    <i className="ri-visa-line"></i> Crédito
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Cambio Logica - Solo efectivo */}
                                    {metodoPago === 'EFECTIVO' && (
                                        <div className="animate-in fade-in slide-in-from-top-2 duration-200 bg-slate-50 p-3 rounded-xl border border-dashed border-slate-300">
                                            <div className="flex gap-4 items-center">
                                                <div className="flex-1">
                                                    <label className="block text-xs font-medium text-slate-500 mb-1">Recibido:</label>
                                                    <CurrencyInput
                                                        value={montoRecibido}
                                                        onChange={(val: number) => {
                                                            setMontoRecibido(val);
                                                            setCambio(Math.max(0, val - (vehiculoSalida.total || 0)));
                                                        }}
                                                        className="w-full pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 outline-none font-bold text-slate-800"
                                                        placeholder="Ingrese monto"
                                                    />
                                                </div>
                                                <div className="flex-1 text-right">
                                                     <label className="block text-xs font-medium text-slate-500 mb-1">Cambio:</label>
                                                     <div className={`text-xl font-black ${cambio > 0 ? 'text-blue-600' : 'text-slate-400'}`}>
                                                        {formatCOP(cambio)}
                                                     </div>
                                                </div>
                                            </div>
                                            {montoRecibido > 0 && montoRecibido < (vehiculoSalida.total || 0) && (
                                                <p className="text-xs text-red-500 mt-2 font-medium">⚠️ Monto insuficiente</p>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* ========= FACTURACIÓN ELECTRÓNICA ========= */}
                                <div className="space-y-3 border-t border-slate-200 pt-4">
                                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider">
                                        <i className="ri-file-text-line mr-1"></i>
                                        Factura Electrónica
                                    </label>
                                    
                                    {/* Botones de selección de tipo de factura */}
                                    <div className="grid grid-cols-2 gap-2">
                                        <button
                                            onClick={() => { setInvoiceType('CONSUMIDOR_FINAL'); setClienteData(null); setClienteDocumento(''); }}
                                            className={`px-3 py-3 rounded-lg border text-sm font-medium transition flex flex-col items-center gap-1 ${
                                                invoiceType === 'CONSUMIDOR_FINAL' 
                                                    ? 'bg-blue-50 border-blue-300 text-blue-700 ring-2 ring-blue-200' 
                                                    : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                                            }`}
                                        >
                                            <i className="ri-user-line text-lg"></i>
                                            <span>Consumidor Final</span>
                                            <span className="text-xs opacity-70">(Rápido)</span>
                                        </button>
                                        <button
                                            onClick={() => setInvoiceType('CLIENTE_IDENTIFICADO')}
                                            className={`px-3 py-3 rounded-lg border text-sm font-medium transition flex flex-col items-center gap-1 ${
                                                invoiceType === 'CLIENTE_IDENTIFICADO' 
                                                    ? 'bg-green-50 border-green-300 text-green-700 ring-2 ring-green-200' 
                                                    : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                                            }`}
                                        >
                                            <i className="ri-user-star-line text-lg"></i>
                                            <span>Cliente Identificado</span>
                                            <span className="text-xs opacity-70">(Con datos)</span>
                                        </button>
                                    </div>

                                    {/* Formulario de datos del cliente */}
                                    {invoiceType === 'CLIENTE_IDENTIFICADO' && (
                                        <div className="animate-in fade-in slide-in-from-top-2 duration-200 bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-xl border border-green-200 space-y-3">
                                            {/* Búsqueda por documento */}
                                            <div className="flex gap-2">
                                                <div className="flex-1">
                                                    <label className="block text-xs font-medium text-slate-600 mb-1">
                                                        Número de Documento
                                                    </label>
                                                    <input
                                                        type="text"
                                                        placeholder="Ingrese cédula o NIT"
                                                        value={clienteDocumento}
                                                        onChange={(e) => setClienteDocumento(e.target.value)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                handleBuscarCliente();
                                                            }
                                                        }}
                                                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                                                    />
                                                </div>
                                                <button
                                                    onClick={handleBuscarCliente}
                                                    disabled={isSearchingClient || clienteDocumento.length < 5}
                                                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed mt-5"
                                                >
                                                    {isSearchingClient ? (
                                                        <i className="ri-loader-4-line animate-spin"></i>
                                                    ) : (
                                                        <i className="ri-search-line"></i>
                                                    )}
                                                </button>
                                            </div>

                                            {/* Cliente encontrado o formulario nuevo */}
                                            {clienteEncontrado && (
                                                <div className="bg-white p-3 rounded-lg border border-green-300 text-sm relative">
                                                    <div className="flex justify-between items-start">
                                                        <div>
                                                            <p className="font-medium text-green-700 flex items-center gap-1">
                                                                <i className="ri-checkbox-circle-fill"></i>
                                                                Cliente encontrado
                                                            </p>
                                                            <p className="text-slate-700 font-bold">{clienteEncontrado.nombre}</p>
                                                            <p className="text-slate-500 text-xs">{clienteEncontrado.email || 'Sin email'}</p>
                                                        </div>
                                                        <button
                                                            onClick={() => {
                                                                setClienteEncontrado(null);
                                                                setClienteData((prev) => prev ? { ...prev, saveClient: true } : null);
                                                            }}
                                                            className="text-blue-600 hover:text-blue-800 text-xs font-bold underline"
                                                        >
                                                            Editar
                                                        </button>
                                                    </div>
                                                </div>
                                            )}

                                            {clienteData && !clienteEncontrado && (
                                                <div className="space-y-2">
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <div>
                                                            <label className="text-xs text-slate-600">Tipo Doc</label>
                                                            <select
                                                                value={clienteData.tipoDocumento}
                                                                onChange={(e) => setClienteData({...clienteData, tipoDocumento: e.target.value})}
                                                                className="w-full px-2 py-2 border rounded-lg text-sm"
                                                            >
                                                                <option value="CC">Cédula</option>
                                                                <option value="NIT">NIT</option>
                                                                <option value="CE">Cédula Ext.</option>
                                                                <option value="PASAPORTE">Pasaporte</option>
                                                            </select>
                                                        </div>
                                                        <div>
                                                            <label className="text-xs text-slate-600">Documento</label>
                                                            <input
                                                                type="text"
                                                                value={clienteData.numeroDocumento}
                                                                readOnly
                                                                className="w-full px-2 py-2 border rounded-lg text-sm bg-slate-100"
                                                            />
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <label className="text-xs text-slate-600">Nombre Completo *</label>
                                                        <input
                                                            type="text"
                                                            placeholder="Nombre o Razón Social"
                                                            value={clienteData.nombre}
                                                            onChange={(e) => setClienteData({...clienteData, nombre: e.target.value})}
                                                            className="w-full px-3 py-2 border rounded-lg text-sm"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="text-xs text-slate-600">Email (opcional)</label>
                                                        <input
                                                            type="email"
                                                            placeholder="correo@ejemplo.com"
                                                            value={clienteData.email || ''}
                                                            onChange={(e) => setClienteData({...clienteData, email: e.target.value})}
                                                            className="w-full px-3 py-2 border rounded-lg text-sm"
                                                        />
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <div>
                                                            <label className="text-xs text-slate-600">Teléfono</label>
                                                            <input
                                                                type="text"
                                                                placeholder="300 000 0000"
                                                                value={clienteData.telefono || ''}
                                                                onChange={(e) => setClienteData({...clienteData, telefono: e.target.value})}
                                                                className="w-full px-3 py-2 border rounded-lg text-sm"
                                                            />
                                                        </div>
                                                        <div className="relative">
                                                            <label className="text-xs text-slate-600">Ciudad</label>
                                                            <input
                                                                type="text"
                                                                placeholder="Escribe para buscar..."
                                                                value={clienteData.ciudad || ''}
                                                                onChange={async (e) => {
                                                                    const val = e.target.value;
                                                                    setClienteData({...clienteData, ciudad: val, ciudadId: undefined});
                                                                    if (val.length > 2) {
                                                                        setIsSearchingCity(true);
                                                                        try {
                                                                            const res = await searchFactusMunicipalities(val);
                                                                            setCitySuggestions(res);
                                                                            setShowCitySuggestions(true);
                                                                        } catch(err) { console.error(err); }
                                                                        setIsSearchingCity(false);
                                                                    } else {
                                                                        setShowCitySuggestions(false);
                                                                    }
                                                                }}
                                                                onBlur={() => setTimeout(() => setShowCitySuggestions(false), 200)}
                                                                className="w-full px-3 py-2 border rounded-lg text-sm"
                                                            />
                                                            {showCitySuggestions && citySuggestions.length > 0 && (
                                                                <ul className="absolute z-50 bg-white border border-slate-200 rounded-lg shadow-lg w-full max-h-48 overflow-y-auto mt-1 bottom-full mb-1">
                                                                    {citySuggestions.map((city) => (
                                                                        <li 
                                                                            key={city.id}
                                                                            onClick={() => {
                                                                                setClienteData({...clienteData, ciudad: city.name, ciudadId: String(city.id)});
                                                                                setShowCitySuggestions(false);
                                                                            }}
                                                                            className="px-3 py-2 hover:bg-slate-50 cursor-pointer text-sm text-slate-700 border-b border-slate-50 last:border-0"
                                                                        >
                                                                            {city.name} <span className="text-xs text-slate-400">({city.department})</span>
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            )}
                                                            {isSearchingCity && <div className="absolute right-3 top-8"><i className="ri-loader-4-line animate-spin text-slate-400"></i></div>}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <label className="text-xs text-slate-600">Dirección</label>
                                                        <input
                                                            type="text"
                                                            placeholder="Calle 123 # 45-67"
                                                            value={clienteData.direccion || ''}
                                                            onChange={(e) => setClienteData({...clienteData, direccion: e.target.value})}
                                                            className="w-full px-3 py-2 border rounded-lg text-sm"
                                                        />
                                                    </div>
                                                    <label className="flex items-center gap-2 text-xs text-slate-600">
                                                        <input
                                                            type="checkbox"
                                                            checked={clienteData.saveClient || false}
                                                            onChange={(e) => setClienteData({...clienteData, saveClient: e.target.checked})}
                                                            className="rounded"
                                                        />
                                                        Guardar cliente para próximas visitas
                                                    </label>
                                                </div>
                                            )}

                                            {!clienteData && (
                                                <p className="text-xs text-slate-500 text-center py-2">
                                                    <i className="ri-information-line"></i>
                                                    Ingrese el documento y presione buscar
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </>
                        ) : (
                            <div className="bg-violet-50 p-6 rounded-xl border border-violet-200 text-center mb-6">
                                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-3 shadow-sm">
                                     <i className="ri-id-card-fill text-3xl text-violet-600"></i>
                                </div>
                                <h3 className="text-xl font-bold text-violet-800 mb-1">Vehículo con Contrato</h3>
                                <p className="text-sm text-violet-600">Salida autorizada sin cobro adicional.</p>
                                <div className="mt-4 inline-flex items-center gap-2 px-3 py-1 bg-white rounded-full text-xs font-bold text-violet-700 border border-violet-100 shadow-sm">
                                     <i className="ri-checkbox-circle-line text-green-500"></i>
                                     Contrato Activo
                                </div>
                            </div>
                        )}

                        {/* Acciones */}
                        <div className="flex gap-3 pt-2">
                             <button
                                onClick={() => { setVehiculoSalida(null); setSearchPlaca(''); setInvoiceType('CONSUMIDOR_FINAL'); setClienteData(null); }}
                                disabled={isSubmitting}
                                className="flex-1 px-4 py-3 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 font-medium disabled:opacity-50"
                            >
                                Atrás
                            </button>
                            <button
                                onClick={handleConfirmarPago}
                                disabled={
                                  isSubmitting || 
                                  (!vehiculoSalida.movement.usaCupoContrato && metodoPago === 'EFECTIVO' && montoRecibido < (vehiculoSalida.total || 0)) ||
                                  (!vehiculoSalida.movement.usaCupoContrato && invoiceType === 'CLIENTE_IDENTIFICADO' && (!clienteData || !clienteData.nombre))
                                }
                                className="flex-[2] px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl shadow-lg shadow-green-200 font-bold text-lg flex items-center justify-center gap-2 disabled:opacity-50"
                            >
                                {isSubmitting ? <i className="ri-loader-4-line animate-spin"></i> : (
                                  vehiculoSalida.movement.usaCupoContrato ? (
                                        <>
                                           <i className="ri-logout-box-line"></i>
                                           Registrar Salida
                                        </>
                                    ) : (
                                        <>
                                           <i className="ri-check-double-line"></i>
                                           Cobrar {formatCOP(vehiculoSalida.total)}
                                        </>
                                    )
                                )}
                            </button>
                        </div>
                    </div>
                )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Advertencia - Sin Turno Activo */}
      {showNoShiftWarning && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="bg-amber-500 text-white px-6 py-4">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <i className="ri-error-warning-line text-xl"></i>
                Acción No Disponible
              </h2>
            </div>
            <div className="p-6">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-store-2-line text-2xl text-amber-600"></i>
                </div>
                <div>
                  <p className="font-semibold text-slate-800 mb-1">Debes abrir un turno primero</p>
                  <p className="text-sm text-slate-500">
                    Para registrar entradas o salidas de vehículos, es necesario tener un turno (caja) activo.
                  </p>
                </div>
              </div>
              <div className="bg-slate-50 rounded-xl p-4 mb-6">
                <p className="text-sm text-slate-600">
                  <i className="ri-lightbulb-line text-amber-500 mr-1"></i>
                  Busca el botón <span className="font-semibold text-teal-600">"Abrir Caja"</span> en la barra superior para iniciar tu turno.
                </p>
              </div>
              <button
                onClick={() => setShowNoShiftWarning(false)}
                className="w-full py-3 bg-slate-800 hover:bg-slate-900 text-white rounded-xl font-medium transition"
              >
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
